'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AirportAutocomplete from '@/components/common/AirportAutocomplete';
import type { SearchParams } from './SearchInteractive';

interface FlightSearchFormProps {
  onSearch: (params: SearchParams) => void;
  initialParams: SearchParams;
}

const FlightSearchForm = ({ onSearch, initialParams }: FlightSearchFormProps) => {
  const [tripType, setTripType] = useState<'one-way' | 'round-trip' | 'multi-city'>(initialParams.tripType);
  const [from, setFrom] = useState(initialParams.from);
  const [to, setTo] = useState(initialParams.to);
  const [departDate, setDepartDate] = useState(initialParams.departDate);
  const [returnDate, setReturnDate] = useState(initialParams.returnDate || '');
  const [passengers, setPassengers] = useState(initialParams.passengers);
  const [cabinClass, setCabinClass] = useState(initialParams.cabinClass);
  const [showPassengers, setShowPassengers] = useState(false);
  const [multiCityFlights, setMultiCityFlights] = useState(initialParams.multiCity || [
    { from: '', to: '', date: '' },
    { from: '', to: '', date: '' },
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({
      tripType,
      from,
      to,
      departDate,
      returnDate: tripType === 'round-trip' ? returnDate : undefined,
      passengers,
      cabinClass,
      multiCity: tripType === 'multi-city' ? multiCityFlights : undefined,
    });
  };

  const addMultiCityFlight = () => {
    setMultiCityFlights([...multiCityFlights, { from: '', to: '', date: '' }]);
  };

  const removeMultiCityFlight = (index: number) => {
    setMultiCityFlights(multiCityFlights.filter((_, i) => i !== index));
  };

  const updateMultiCityFlight = (index: number, field: string, value: string) => {
    const updated = [...multiCityFlights];
    updated[index] = { ...updated[index], [field]: value };
    setMultiCityFlights(updated);
  };

  const totalPassengers = passengers.adults + passengers.children + passengers.infants;

  return (
    <div className="bg-card rounded-xl shadow-3d p-6 lg:p-8 transform-3d shine-effect border-4 border-accent/20">
      {/* Trip Type Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {(['round-trip', 'one-way', 'multi-city'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setTripType(type)}
            className={`px-4 py-2 rounded-lg text-sm font-black transition-all duration-200 ${
              tripType === type
                ? 'gradient-gold-shiny text-primary shadow-3d border-2 border-accent'
                : 'bg-surface text-foreground hover:bg-muted font-bold'
            }`}
          >
            {type === 'round-trip' ? 'Round Trip' : type === 'one-way' ? 'One Way' : 'Multi-City'}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {tripType !== 'multi-city' ? (
          <div className="space-y-4">
            {/* From/To Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AirportAutocomplete
                value={from}
                onChange={setFrom}
                placeholder="City or airport"
                label="From"
                required
              />

              <AirportAutocomplete
                value={to}
                onChange={setTo}
                placeholder="City or airport"
                label="To"
                required
              />
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-black text-foreground mb-2">
                  Departure
                </label>
                <div className="relative">
                  <Icon name="CalendarIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={departDate}
                    onChange={(e) => setDepartDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg"
                    required
                  />
                </div>
              </div>

              {tripType === 'round-trip' && (
                <div>
                  <label className="block text-sm font-black text-foreground mb-2">
                    Return
                  </label>
                  <div className="relative">
                    <Icon name="CalendarIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg"
                      required
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {multiCityFlights.map((flight, index) => (
              <div key={index} className="p-4 bg-surface rounded-lg space-y-4 shadow-brand-lg border-2 border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-black text-foreground">Flight {index + 1}</span>
                  {multiCityFlights.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeMultiCityFlight(index)}
                      className="text-error hover:text-error/80 transition-colors"
                    >
                      <Icon name="XMarkIcon" size={20} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <AirportAutocomplete
                    value={flight.from}
                    onChange={(value) => updateMultiCityFlight(index, 'from', value)}
                    placeholder="From"
                    required
                  />
                  <AirportAutocomplete
                    value={flight.to}
                    onChange={(value) => updateMultiCityFlight(index, 'to', value)}
                    placeholder="To"
                    required
                  />
                  <div className="relative">
                    <Icon name="CalendarIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      type="date"
                      value={flight.date}
                      onChange={(e) => updateMultiCityFlight(index, 'date', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
            {multiCityFlights.length < 5 && (
              <button
                type="button"
                onClick={addMultiCityFlight}
                className="flex items-center space-x-2 text-primary hover:text-secondary transition-colors font-black"
              >
                <Icon name="PlusIcon" size={20} />
                <span className="text-sm font-black">Add another flight</span>
              </button>
            )}
          </div>
        )}

        {/* Passengers and Class */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="relative">
            <label className="block text-sm font-black text-foreground mb-2">
              Passengers
            </label>
            <button
              type="button"
              onClick={() => setShowPassengers(!showPassengers)}
              className="w-full flex items-center justify-between px-4 py-3 border-2 border-border rounded-lg hover:border-primary transition-all font-bold shadow-brand-lg"
            >
              <div className="flex items-center space-x-2">
                <Icon name="UserGroupIcon" size={20} className="text-muted-foreground pointer-events-none" />
                <span className="text-foreground font-bold">{totalPassengers} Passenger{totalPassengers !== 1 ? 's' : ''}</span>
              </div>
              <Icon name="ChevronDownIcon" size={20} className="text-muted-foreground pointer-events-none" />
            </button>
            
            {showPassengers && (
              <div className="absolute z-10 mt-2 w-full bg-popover border-2 border-border rounded-lg shadow-3d p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-black">Adults</span>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setPassengers({ ...passengers, adults: Math.max(1, passengers.adults - 1) })}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-surface transition-colors"
                    >
                      <Icon name="MinusIcon" size={16} />
                    </button>
                    <span className="w-8 text-center font-medium">{passengers.adults}</span>
                    <button
                      type="button"
                      onClick={() => setPassengers({ ...passengers, adults: Math.min(9, passengers.adults + 1) })}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-surface transition-colors"
                    >
                      <Icon name="PlusIcon" size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-black">Children (2-11)</span>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setPassengers({ ...passengers, children: Math.max(0, passengers.children - 1) })}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-surface transition-colors"
                    >
                      <Icon name="MinusIcon" size={16} />
                    </button>
                    <span className="w-8 text-center font-medium">{passengers.children}</span>
                    <button
                      type="button"
                      onClick={() => setPassengers({ ...passengers, children: Math.min(9, passengers.children + 1) })}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-surface transition-colors"
                    >
                      <Icon name="PlusIcon" size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-black">Infants (0-2)</span>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setPassengers({ ...passengers, infants: Math.max(0, passengers.infants - 1) })}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-surface transition-colors"
                    >
                      <Icon name="MinusIcon" size={16} />
                    </button>
                    <span className="w-8 text-center font-medium">{passengers.infants}</span>
                    <button
                      type="button"
                      onClick={() => setPassengers({ ...passengers, infants: Math.min(passengers.adults, passengers.infants + 1) })}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-border hover:bg-surface transition-colors"
                    >
                      <Icon name="PlusIcon" size={16} />
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassengers(false)}
                  className="w-full py-2 bg-primary text-white rounded-lg hover:bg-secondary transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-black text-foreground mb-2">
              Cabin Class
            </label>
            <select
              value={cabinClass}
              onChange={(e) => setCabinClass(e.target.value as any)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            >
              <option value="economy">Economy</option>
              <option value="premium-economy">Premium Economy</option>
              <option value="business">Business</option>
              <option value="first">First Class</option>
            </select>
          </div>
        </div>

        {/* Search Button */}
        <button
          type="submit"
          className="w-full mt-6 py-4 bg-primary text-white font-heading font-black text-lg rounded-lg hover:bg-primary/90 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
        >
          <Icon name="MagnifyingGlassIcon" size={24} />
          <span>Search Flights</span>
        </button>
      </form>
    </div>
  );
};

export default FlightSearchForm;
